import { IconName } from '../../../../components/Icon';
import {
  ISearchItemFieldsFragment,
  ScheduleType,
  SearchEntityType,
} from '../../../../utils/apollo/graphqlSchema';
import { UnreachableCaseError } from 'ts-essentials';
import { appRoutes } from '../../../../path';
import {
  getUrlForDataSharing,
  SearchParamsDataSharingEnum,
} from '../../../../utils/searchParamsDataSharing';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import { queryTypeConfigLookup } from '../../../../utils/queryTypeLookup';
import { ISearchConfig, SearchableEntityTypes } from '../types';
import { getQueryTypePagePath } from '../../../../utils';

export const getIconNameBySearchEntityType = (
  searchEntity: ISearchItemFieldsFragment['entity'],
): IconName => {
  switch (searchEntity.type) {
    case SearchEntityType.Analysis:
      assertCondition(searchEntity.__typename === 'SearchAnalysis');
      return queryTypeConfigLookup[searchEntity.queryType].icon;
    case SearchEntityType.Dashboard:
      return 'dashboard';
    case SearchEntityType.Cohort:
      return 'named-cohort';
    case SearchEntityType.Filter:
      return 'filter';
    case SearchEntityType.Measure:
      return 'es-measure';
    case SearchEntityType.VirtualEvent:
      return 'es-virtual-event';
    case SearchEntityType.BucketDef:
      return 'histogram';
    case SearchEntityType.Field:
      return 'es-field';
    case SearchEntityType.Event:
      return 'es-basic-event';
    case SearchEntityType.Schedule:
      return 'schedule';
    case SearchEntityType.Workspace:
      return 'workspace';
    default:
      throw new UnreachableCaseError(searchEntity.type);
  }
};

export const getUrlBySearchEntityType = (
  type: SearchEntityType,
  scheduleType?: ScheduleType,
) => {
  switch (type) {
    case SearchEntityType.Analysis:
      return appRoutes.report.routePrefix;
    case SearchEntityType.Dashboard:
      return appRoutes.dashboard.routePrefix;
    case SearchEntityType.Cohort:
      return appRoutes.cohorts.view;
    case SearchEntityType.Measure:
      return appRoutes.dictionary.measure;
    case SearchEntityType.Filter:
      return appRoutes.dictionary.filter;
    case SearchEntityType.VirtualEvent:
      return appRoutes.dictionary.virtualEvent;
    case SearchEntityType.BucketDef:
      return appRoutes.dictionary.buckets;
    case SearchEntityType.Field:
      return appRoutes.dictionary.field;
    case SearchEntityType.Event:
      return appRoutes.dictionary.event;
    case SearchEntityType.Schedule:
      assertNotNil(scheduleType);
      if (scheduleType === ScheduleType.Analysis) {
        return appRoutes.schedules.report;
      } else if (scheduleType === ScheduleType.Dashboard) {
        return appRoutes.schedules.dashboard;
      } else if (scheduleType === ScheduleType.BrazeCohortImport) {
        return appRoutes.cohorts.view;
      } else {
        throw new UnreachableCaseError(scheduleType);
      }
    case SearchEntityType.Workspace:
      return appRoutes.workspaces.routePrefix;
    default:
      throw new UnreachableCaseError(type);
  }
};

export const getClickRedirectUrl = (
  entity: ISearchItemFieldsFragment['entity'],
) => {
  const prefixUrl =
    entity.__typename !== 'SearchSchedule'
      ? getUrlBySearchEntityType(entity.type)
      : '';

  switch (entity.__typename) {
    case 'IdSearchEntity':
      if (entity.type === SearchEntityType.Workspace) {
        assertNotNil(entity.nullableBaseId);
        return `${prefixUrl}/${entity.nullableBaseId}`;
      } else {
        const redirectUrl = getUrlForDataSharing(prefixUrl, {
          type: SearchParamsDataSharingEnum.Dictionary,
          // special case for binning that binning doesn't have schema.
          schemaId: entity.nullableSchema?.id ?? '',
          term: entity.name,
        });
        assertNotNil(redirectUrl);
        return redirectUrl;
      }
    case 'KeySearchEntity':
      const keySearchUrl = getUrlForDataSharing(prefixUrl, {
        type: SearchParamsDataSharingEnum.Dictionary,
        // special case for binning that binning doesn't have schema.
        schemaId: entity.schema.id,
        term: entity.name,
      });
      assertNotNil(keySearchUrl);
      return keySearchUrl;
    case 'SearchDashboard':
      return `${prefixUrl}/${entity.baseId}`;
    case 'SearchAnalysis':
      return getQueryTypePagePath(entity.queryType, entity.id);
    case 'SearchDashboardFolder':
      return `${prefixUrl}/${entity.id}`;
    case 'SearchSchedule':
      const schedulePrefixUrl = getUrlBySearchEntityType(
        entity.type,
        entity.scheduleType,
      );
      if (entity.scheduleType === ScheduleType.Dashboard) {
        const scheduleUrl = getUrlForDataSharing(schedulePrefixUrl, {
          type: SearchParamsDataSharingEnum.DashboardSchedule,
          schemaId: '',
          term: entity.name,
        });
        assertNotNil(scheduleUrl);
        return scheduleUrl;
      }
      const scheduleUrl = getUrlForDataSharing(schedulePrefixUrl, {
        type: SearchParamsDataSharingEnum.Dictionary,
        // special case for binning that binning doesn't have schema.
        schemaId: entity.nullableSchema?.id ?? '',
        term: entity.name,
      });
      assertNotNil(scheduleUrl);
      return scheduleUrl;
    default:
      throw new UnreachableCaseError(entity);
  }
};

export const defaultSearchConfig: ISearchConfig = {
  // not implemented on Kobol yet
  // filterByFavoriteOrMyOwn: true,
  schemaIds: true,
  queryTypes: true,
  editorIds: true,
  categoryIds: true,
  failedReports: false,
};

export const getAdvancedSearchConfig = (
  types: SearchableEntityTypes[],
): ISearchConfig => {
  if (types.length === 0) {
    return defaultSearchConfig;
  } else {
    const searchType = types[0];
    switch (searchType) {
      case SearchEntityType.Analysis:
        return {
          ...defaultSearchConfig,
          categoryIds: false,
        };
      case SearchEntityType.Dashboard:
        return {
          ...defaultSearchConfig,
          categoryIds: false,
          schemaIds: false,
          queryTypes: false,
        };
      case SearchEntityType.Cohort:
        return {
          ...defaultSearchConfig,
          queryTypes: false,
        };
      case SearchEntityType.Measure:
        return {
          ...defaultSearchConfig,
          queryTypes: false,
        };
      default:
        throw new UnreachableCaseError(searchType);
    }
  }
};
